// src/features/players/components/SettingsTab.tsx

import {
  Card, Group, Text, Button, Stack, Box, Switch, Select, Divider,
} from '@mantine/core';
import { useForm, useController, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { notifications } from '@mantine/notifications';
import { settingsFormSchema } from '../schemas/PlayerForm.schema';
import { useUpdatePlayer } from '../usePlayerMutations';
import { PlayerUtils } from '../PlayerUtils';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  PLAYER_VIDEO_AUDIO_MODES,
  PLAYER_VIDEO_AUDIO_MODE_LABELS,
  SKIP_SECONDS_OPTIONS,
} from '@/lib/constants/player';
import type { PlayerViewData, PlayerSettingsFormData } from '@/lib/types/Player';
import { FormSaveBar } from '@/shared/components/FormSaveBar';

interface SettingsTabProps {
  player: PlayerViewData;
}

export function SettingsTab({ player }: SettingsTabProps) {
  const playerId = player.core.playerId;
  const updateMutation = useUpdatePlayer(playerId);
  const { selectedCustomer } = useAuth();
  const allowDvr = selectedCustomer?.cfg.quota.features.account.allowDvr === true;

  const { control, handleSubmit, formState: { isDirty, errors }, reset, watch } = useForm<PlayerSettingsFormData>({
    resolver: zodResolver(settingsFormSchema),
    defaultValues: PlayerUtils.transformToSettingsForm(player),
  });

  const allowSkipSecs = watch('allowSkipSecs');

  const onSubmit = async (data: PlayerSettingsFormData) => {
    try {
      const req = PlayerUtils.transformSettingsFormToUpdate(data, player);
      await updateMutation.mutateAsync(req);
      reset(data);
      notifications.show({ title: 'Saved', message: 'Settings updated', color: 'green' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save changes';
      notifications.show({ title: 'Error', message, color: 'red' });
    }
  };

  const onInvalid = (fieldErrors: typeof errors) => {
    const firstError = Object.values(fieldErrors).find(e => e?.message);
    if (firstError?.message) {
      notifications.show({ title: 'Validation Error', message: firstError.message, color: 'orange' });
    }
  };

  const isPending = updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <Stack gap="xl" maw={800}>
        <Text fw={600} size="lg">Settings</Text>
        <FormSaveBar isDirty={isDirty} isPending={isPending} onDiscard={() => reset()} />

        <Card shadow="sm" padding="lg" withBorder>
          <Stack gap="md">
            {/* Playback */}
            <Text fw={600} size="sm" c="dimmed" tt="uppercase">Playback</Text>
            <SwitchField control={control} name="autoplay" label="Autoplay" />
            <SwitchField control={control} name="loop" label="Loop" />
            <MutedField control={control} />
            <SwitchField control={control} name="allowSeekForward" label="Allow Seek Forward" />
            <SwitchField control={control} name="resumePlayback" label="Resume Playback" description="Remember playback position across sessions" />
            <SwitchField control={control} name="playsinline" label="Play Inline" description="Play inline on mobile instead of fullscreen" />

            <Divider label="Display" labelPosition="left" />
            <SwitchField control={control} name="allowDownload" label="Allow Download" />
            <SwitchField control={control} name="showTitle" label="Show Title" />
            <SwitchField control={control} name="showCaptions" label="Show Captions" />
            {allowDvr && <SwitchField control={control} name="showDvr" label="Show DVR" />}

            <Divider label="Player Controls" labelPosition="left" />
            <SwitchField control={control} name="fullscreen" label="Allow Fullscreen" />
            <SwitchField control={control} name="allowPIP" label="Allow Picture-in-Picture" />
            <SwitchField control={control} name="allowCasting" label="Allow Casting" />
            <SwitchField control={control} name="allowPlaybackRate" label="Allow Playback Speed" />
            <SwitchField
              control={control}
              name="showViewerCount"
              label="Show Live Viewer Count"
              description="Displays a live 'watching now' badge to your audience during live streams"
            />
            <SkipSecsField control={control} enabled={allowSkipSecs != null} />

            <Divider label="Media Mode" labelPosition="left" />
            <VideoAudioModeField control={control} />
          </Stack>
        </Card>
      </Stack>
    </form>
  );
}

// --- Reusable switch field ---

function SwitchField({ control, name, label, description }: {
  control: Control<PlayerSettingsFormData>; name: keyof PlayerSettingsFormData; label: string; description?: string;
}) {
  const { field } = useController({ control, name });
  return (
    <Switch
      label={label}
      description={description}
      checked={field.value as boolean}
      onChange={(e) => field.onChange(e.currentTarget.checked)}
    />
  );
}

// --- Muted toggle (volume 0/100) ---

function MutedField({ control }: { control: Control<PlayerSettingsFormData> }) {
  const { field } = useController({ control, name: 'volume' });
  const isMuted = field.value === 0;
  return (
    <Switch
      label="Muted"
      description="Start with audio muted"
      checked={isMuted}
      onChange={(e) => field.onChange(e.currentTarget.checked ? 0 : 100)}
    />
  );
}

// --- Skip seconds ---

function SkipSecsField({ control, enabled }: { control: Control<PlayerSettingsFormData>; enabled: boolean }) {
  const { field } = useController({ control, name: 'allowSkipSecs' });

  const handleToggle = (checked: boolean) => {
    field.onChange(checked ? SKIP_SECONDS_OPTIONS[0] : null);
  };

  return (
    <Stack gap="xs">
      <Switch
        label="Skip Forward"
        description="Enable keyboard/gesture skip forward using YouTube-style shortcuts"
        checked={enabled}
        onChange={(e) => handleToggle(e.currentTarget.checked)}
      />
      {enabled && (
        <Select
          label="Skip Duration (seconds)"
          value={String(field.value ?? SKIP_SECONDS_OPTIONS[0])}
          onChange={(val) => field.onChange(val ? Number(val) : null)}
          data={SKIP_SECONDS_OPTIONS.map(s => ({ value: String(s), label: `${s} seconds` }))}
          w={200}
          ml="md"
        />
      )}
    </Stack>
  );
}

// --- Video/Audio mode ---

function VideoAudioModeField({ control }: { control: Control<PlayerSettingsFormData> }) {
  const { field } = useController({ control, name: 'videoAudioMode' });
  return (
    <Select
      label="Media Mode"
      value={field.value}
      onChange={(val) => val && field.onChange(val)}
      data={PLAYER_VIDEO_AUDIO_MODES.map(m => ({ value: m, label: PLAYER_VIDEO_AUDIO_MODE_LABELS[m] }))}
      w={250}
    />
  );
}

// The legacy responsive/width/height sizing settings are not consumed by the platform
// player or content service; they stay in the form model and transforms so stored values
// round-trip unchanged, but are no longer editable here.

